// Public live-chat widget — floating bubble, dark/teal VoiceFabric branding.
// Anonymous visitors give name + one contact, then chat; AI or staff replies.
// Session persists in localStorage so a returning visitor keeps their thread.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'vf_livechat_session';

export const ChatWidget = () => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('open');
  const [agentName, setAgentName] = useState(null);
  const [form, setForm] = useState({ name: '', contact: '', message: '' });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const lastAtRef = useRef('');

  useEffect(() => {
    axios.get(`${API}/livechat/site-config`).then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  const poll = useCallback(async (s) => {
    if (!s) return;
    try {
      const r = await axios.get(`${API}/livechat/${s.session_id}/messages`, {
        params: { visitor_token: s.visitor_token, after: lastAtRef.current },
      });
      if (r.data.messages?.length) {
        lastAtRef.current = r.data.messages[r.data.messages.length - 1].created_at;
        setMessages((prev) => [...prev, ...r.data.messages.filter((m) => !prev.some((p) => p.id === m.id))]);
      }
      setStatus(r.data.status);
      setAgentName(r.data.agent_name);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 403) {
        localStorage.removeItem(LS_KEY);
        setSession(null); setMessages([]); lastAtRef.current = '';
      }
    }
  }, []);

  useEffect(() => {
    if (!open || !session) return undefined;
    lastAtRef.current = '';
    setMessages([]);
    poll(session);
    const t = setInterval(() => poll(session), 3000);
    return () => clearInterval(t);
  }, [open, session, poll]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const start = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/livechat/start`, {
        domain_id: cfg.domain_id, name: form.name, contact: form.contact, message: form.message,
      });
      const s = { session_id: r.data.session_id, visitor_token: r.data.visitor_token };
      localStorage.setItem(LS_KEY, JSON.stringify(s));
      setSession(s);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start the chat — try again.');
    } finally { setBusy(false); }
  };

  const send = async () => {
    const content = draft.trim();
    if (!content || !session) return;
    setDraft('');
    setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: 'visitor', content, created_at: new Date().toISOString() }]);
    try {
      await axios.post(`${API}/livechat/${session.session_id}/message`, {
        visitor_token: session.visitor_token, content,
      });
    } catch { setError('Message failed to send — try again.'); }
  };

  if (!cfg?.enabled) return null;
  const accent = cfg.accent || '#14b8a6';

  return (
    <div className="fixed bottom-5 right-5 z-[80]" data-testid="livechat-widget">
      {open && (
        <div className="mb-3 w-[340px] max-w-[calc(100vw-2.5rem)] rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden bg-[#0A0F1C] text-slate-100" data-testid="livechat-panel">
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: accent }}>
            <div className="flex items-center gap-2.5">
              {cfg.logo_url && (
                <img src={cfg.logo_url} alt="" className="w-7 h-7 rounded-lg object-contain bg-white p-0.5"
                  onError={(e) => { e.target.style.display = 'none'; }} data-testid="livechat-logo" />
              )}
              <div>
                <p className="font-bold text-sm text-white" data-testid="livechat-title">{cfg.title}</p>
                <p className="text-[11px] text-white/85">
                  {agentName ? `${agentName} is with you` : (cfg.subtitle || 'AI + real humans · replies in seconds')}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="livechat-close-btn" className="text-white/80 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {!session ? (
            <div className="p-4 space-y-2.5" data-testid="livechat-prechat-form">
              <p className="text-xs text-slate-300">{cfg.greeting}</p>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name *" data-testid="livechat-name-input"
                className="w-full text-sm rounded-lg bg-white/5 border border-white/10 px-3 py-2 placeholder:text-slate-500 focus:outline-none focus:border-teal-500" />
              <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder="Email or mobile *" data-testid="livechat-contact-input"
                className="w-full text-sm rounded-lg bg-white/5 border border-white/10 px-3 py-2 placeholder:text-slate-500 focus:outline-none focus:border-teal-500" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help?" rows={2} data-testid="livechat-first-message-input"
                className="w-full text-sm rounded-lg bg-white/5 border border-white/10 px-3 py-2 placeholder:text-slate-500 focus:outline-none focus:border-teal-500 resize-none" />
              {error && <p className="text-[11px] text-red-400" data-testid="livechat-error">{error}</p>}
              <button onClick={start} disabled={busy} data-testid="livechat-start-btn" style={{ background: accent }}
                className="w-full rounded-lg text-white font-bold text-sm py-2 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:brightness-110">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {cfg.after_hours ? 'Leave a message' : 'Start chat'}
              </button>
            </div>
          ) : (
            <>
              <div className="h-72 overflow-y-auto p-3 space-y-2 bg-[#0A0F1C]" data-testid="livechat-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                    <div style={m.role === 'visitor' ? { background: accent } : undefined}
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-snug ${
                      m.role === 'visitor' ? 'text-white'
                        : m.role === 'staff' ? 'bg-white/15 text-white'
                          : 'bg-white/5 border border-white/10 text-slate-200'}`}>
                      {m.role !== 'visitor' && (
                        <p className="text-[10px] font-bold opacity-60 mb-0.5">{m.sender_name || (m.role === 'ai' ? 'AI Assistant' : 'Team')}</p>
                      )}
                      {m.content}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {status === 'closed' ? (
                <p className="px-4 py-3 text-[11px] text-slate-400 border-t border-white/10" data-testid="livechat-closed-note">
                  This chat has ended. Refresh to start a new one.
                </p>
              ) : (
                <div className="p-2.5 border-t border-white/10 flex gap-2">
                  <input value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && send()}
                    placeholder="Type a message…" data-testid="livechat-message-input"
                    className="flex-1 text-sm rounded-lg bg-white/5 border border-white/10 px-3 py-2 placeholder:text-slate-500 focus:outline-none focus:border-teal-500" />
                  <button onClick={send} data-testid="livechat-send-btn" style={{ background: accent }}
                    className="rounded-lg text-white px-3 inline-flex items-center hover:brightness-110">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="livechat-bubble" style={{ background: accent }}
        className="ml-auto flex h-14 w-14 items-center justify-center rounded-full text-white shadow-xl hover:scale-105 transition-transform">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </div>
  );
};

export default ChatWidget;
